// 🔥 가격 스냅샷 백업/복구 서비스 - 프리미엄급 구현
// [배경] 관리자 페이지에서 가격 데이터의 안전한 스냅샷 백업, 검증, 미리보기, 부분 복구, 롤백 기능 제공
// Firestore 컬렉션: price_snapshots (메타데이터), price_snapshots/{snapshotId}/data (실제 데이터)

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  writeBatch,
  query,
  orderBy,
  limit,
  where,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { UnifiedPriceDocument, DeviceType, DeviceBrand } from '../types/deviceCatalog';
import {
  PriceSnapshot,
  SnapshotBreakdown,
  SnapshotDataChunk,
  SnapshotFilter,
  SnapshotListOptions,
  PriceDiffResult,
  PriceDiffItem,
  DiffSummary,
  DiffType,
  ValidationResult,
  ValidationIssue,
  ValidationStatus,
  RestoreOptions,
  RestoreResult,
  RestoreProgress,
  CreateSnapshotRequest,
  CreateSnapshotResult,
  SNAPSHOT_CONSTANTS
} from '../types/priceSnapshot';

// ============================================================
// 🔄 가격 스냅샷 서비스 클래스
// ============================================================

/**
 * 가격 스냅샷 백업/복구 서비스
 * - 스냅샷 생성 (전체/부분)
 * - 스냅샷 목록 조회
 * - 현재 데이터와 스냅샷 비교 (Diff)
 * - 데이터 검증
 * - 복구 실행
 */
export class PriceSnapshotService {
  private static readonly COLLECTION = SNAPSHOT_CONSTANTS.COLLECTION;
  private static readonly DATA_SUBCOLLECTION = SNAPSHOT_CONSTANTS.DATA_SUBCOLLECTION;
  private static readonly UNIFIED_PRICES_COLLECTION = 'unified_prices';
  private static readonly CHUNK_SIZE = SNAPSHOT_CONSTANTS.CHUNK_SIZE;

  // 🏷️ 레거시 컬렉션 매핑 (실제 가격 데이터가 저장된 위치)
  private static readonly LEGACY_COLLECTIONS: Array<{
    collection: string;
    brand: DeviceBrand;
    deviceType: DeviceType;
  }> = [
    { collection: 'grade_prices_apple', brand: 'Apple', deviceType: 'smartphone' },
    { collection: 'grade_prices_samsung', brand: 'Samsung', deviceType: 'smartphone' },
    { collection: 'grade_prices_apple_tablet', brand: 'Apple', deviceType: 'tablet' },
    { collection: 'grade_prices_samsung_tablet', brand: 'Samsung', deviceType: 'tablet' },
    { collection: 'grade_prices_apple_wearable', brand: 'Apple', deviceType: 'wearable' }
  ];

  // ============================================================
  // 📦 스냅샷 생성
  // ============================================================

  /**
   * 새 가격 스냅샷 생성
   * 현재 unified_prices 컬렉션의 모든 데이터를 스냅샷으로 저장
   */
  static async createSnapshot(request: CreateSnapshotRequest): Promise<CreateSnapshotResult> {
    const startTime = Date.now();
    const snapshotId = this.generateSnapshotId();

    console.log(`📸 가격 스냅샷 생성 시작: ${snapshotId}`);

    try {
      // 1단계: 현재 가격 데이터 조회
      const prices = await this.fetchAllPrices(request.filter);
      console.log(`📊 조회된 가격 데이터: ${prices.length}개`);

      if (prices.length === 0) {
        return {
          success: false,
          snapshotId,
          error: '백업할 가격 데이터가 없습니다.',
          durationMs: Date.now() - startTime
        };
      }

      // 2단계: 데이터 분석 (브랜드/디바이스별 분포)
      const breakdown = this.calculateBreakdown(prices);
      const fileSize = this.estimateFileSize(prices);

      // 3단계: 데이터를 청크로 분할
      const chunks = this.splitIntoChunks(prices);
      console.log(`📦 데이터 청크 분할: ${chunks.length}개 청크`);

      // 4단계: 검증 실행
      const validationResult = this.validatePriceData(prices);

      // 5단계: 스냅샷 메타데이터 생성
      const snapshot: PriceSnapshot = {
        id: snapshotId,
        createdAt: Timestamp.now(),
        createdBy: request.adminId,
        description: request.description,
        status: 'completed',
        totalPrices: prices.length,
        breakdown,
        fileSize,
        chunkCount: chunks.length,
        validationStatus: validationResult.status,
        validationMessage: validationResult.issues.length > 0 
          ? `${validationResult.errorCount}개 에러, ${validationResult.warningCount}개 경고`
          : '검증 통과',
        validationWarnings: validationResult.issues
          .filter(i => i.severity === 'warning')
          .slice(0, 5)
          .map(i => i.message),
        version: SNAPSHOT_CONSTANTS.CURRENT_VERSION,
        sourceCollection: this.UNIFIED_PRICES_COLLECTION
      };

      // 6단계: Firestore에 저장 (배치 사용)
      const batch = writeBatch(db);

      // 메타데이터 저장
      const snapshotRef = doc(db, this.COLLECTION, snapshotId);
      batch.set(snapshotRef, snapshot);

      await batch.commit();

      // 7단계: 데이터 청크 저장 (별도 배치)
      await this.saveDataChunks(snapshotId, chunks);

      console.log(`✅ 가격 스냅샷 생성 완료: ${snapshotId}`);

      return {
        success: true,
        snapshotId,
        snapshot,
        durationMs: Date.now() - startTime
      };

    } catch (error) {
      console.error(`❌ 가격 스냅샷 생성 실패:`, error);

      // 실패 상태 기록
      try {
        await setDoc(doc(db, this.COLLECTION, snapshotId), {
          id: snapshotId,
          createdAt: Timestamp.now(),
          createdBy: request.adminId,
          description: request.description,
          status: 'failed',
          totalPrices: 0,
          breakdown: this.getEmptyBreakdown(),
          fileSize: 0,
          chunkCount: 0,
          validationStatus: 'error',
          validationMessage: error instanceof Error ? error.message : '알 수 없는 오류',
          version: SNAPSHOT_CONSTANTS.CURRENT_VERSION,
          sourceCollection: this.UNIFIED_PRICES_COLLECTION
        });
      } catch (logError) {
        console.error('실패 상태 기록 오류:', logError);
      }

      return {
        success: false,
        snapshotId,
        error: error instanceof Error ? error.message : '스냅샷 생성 중 오류가 발생했습니다.',
        durationMs: Date.now() - startTime
      };
    }
  }

  /**
   * 데이터 청크 저장
   * ⚠️ Firestore는 undefined 값을 허용하지 않으므로 sanitize 필수
   */
  private static async saveDataChunks(snapshotId: string, chunks: UnifiedPriceDocument[][]): Promise<void> {
    for (let i = 0; i < chunks.length; i++) {
      const chunkId = `chunk_${i.toString().padStart(3, '0')}`;
      
      // 🔧 undefined 값 제거 (Firestore 호환)
      const sanitizedData = chunks[i].map(price => this.sanitizeForFirestore(price));
      
      const chunk: SnapshotDataChunk = {
        id: chunkId,
        chunkIndex: i,
        totalChunks: chunks.length,
        data: sanitizedData,
        recordCount: sanitizedData.length,
        createdAt: Timestamp.now()
      };

      await setDoc(
        doc(db, this.COLLECTION, snapshotId, this.DATA_SUBCOLLECTION, chunkId),
        chunk
      );

      // 진행률 로그 (10청크마다)
      if ((i + 1) % 10 === 0 || i === chunks.length - 1) {
        console.log(`📦 청크 저장 진행: ${i + 1}/${chunks.length}`);
      }
    }
  }

  /**
   * Firestore 저장을 위해 undefined 값 제거
   * Firestore는 undefined 값을 허용하지 않음
   */
  private static sanitizeForFirestore(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (obj instanceof Date) {
      return Timestamp.fromDate(obj);
    }
    
    if (obj instanceof Timestamp) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeForFirestore(item));
    }
    
    if (typeof obj === 'object') {
      const sanitized: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        // undefined 값은 건너뛰기 (Firestore 호환)
        if (value !== undefined) {
          sanitized[key] = this.sanitizeForFirestore(value);
        }
      }
      return sanitized;
    }
    
    return obj;
  }

  // ============================================================
  // 📋 스냅샷 목록 조회
  // ============================================================

  /**
   * 스냅샷 목록 조회
   */
  static async getSnapshots(options: SnapshotListOptions = {}): Promise<PriceSnapshot[]> {
    const {
      limit: limitCount = 50,
      orderBy: orderField = 'createdAt',
      orderDirection = 'desc',
      statusFilter
    } = options;

    try {
      let q = query(
        collection(db, this.COLLECTION),
        orderBy(orderField, orderDirection),
        limit(limitCount)
      );

      if (statusFilter) {
        q = query(
          collection(db, this.COLLECTION),
          where('status', '==', statusFilter),
          orderBy(orderField, orderDirection),
          limit(limitCount)
        );
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as PriceSnapshot[];

    } catch (error) {
      console.error('❌ 스냅샷 목록 조회 실패:', error);
      throw new Error('스냅샷 목록을 불러올 수 없습니다.');
    }
  }

  /**
   * 단일 스냅샷 조회
   */
  static async getSnapshot(snapshotId: string): Promise<PriceSnapshot | null> {
    try {
      const docRef = doc(db, this.COLLECTION, snapshotId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      return { ...docSnap.data(), id: docSnap.id } as PriceSnapshot;
    } catch (error) {
      console.error(`❌ 스냅샷 조회 실패 (${snapshotId}):`, error);
      throw new Error('스냅샷을 불러올 수 없습니다.');
    }
  }

  /**
   * 스냅샷 데이터 조회 (모든 청크 병합)
   */
  static async getSnapshotData(
    snapshotId: string,
    filter?: SnapshotFilter
  ): Promise<UnifiedPriceDocument[]> {
    try {
      const chunksRef = collection(db, this.COLLECTION, snapshotId, this.DATA_SUBCOLLECTION);
      const chunksSnapshot = await getDocs(query(chunksRef, orderBy('chunkIndex', 'asc')));

      let allData: UnifiedPriceDocument[] = [];

      for (const chunkDoc of chunksSnapshot.docs) {
        const chunk = chunkDoc.data() as SnapshotDataChunk;
        allData = [...allData, ...chunk.data];
      }

      // 필터 적용
      if (filter) {
        allData = this.applyFilter(allData, filter);
      }

      return allData;

    } catch (error) {
      console.error(`❌ 스냅샷 데이터 조회 실패 (${snapshotId}):`, error);
      throw new Error('스냅샷 데이터를 불러올 수 없습니다.');
    }
  }

  // ============================================================
  // 📊 Diff (변경사항 비교)
  // ============================================================

  /**
   * 현재 데이터와 스냅샷 비교
   */
  static async compareWithCurrent(
    snapshotId: string,
    filter?: SnapshotFilter
  ): Promise<PriceDiffResult> {
    const startTime = Date.now();

    try {
      // 1. 스냅샷 데이터 조회
      const snapshotData = await this.getSnapshotData(snapshotId, filter);
      const snapshot = await this.getSnapshot(snapshotId);

      if (!snapshot) {
        throw new Error('스냅샷을 찾을 수 없습니다.');
      }

      // 2. 현재 데이터 조회
      const currentData = await this.fetchAllPrices(filter);

      // 3. Diff 계산
      const snapshotMap = new Map(snapshotData.map(p => [p.id, p]));
      const currentMap = new Map(currentData.map(p => [p.id, p]));

      const items: PriceDiffItem[] = [];
      const allIds = new Set([...snapshotMap.keys(), ...currentMap.keys()]);

      for (const id of allIds) {
        const snapshotPrice = snapshotMap.get(id);
        const currentPrice = currentMap.get(id);

        const diffItem = this.calculateDiffItem(id, currentPrice, snapshotPrice);
        items.push(diffItem);
      }

      // 4. 요약 계산
      const summary = this.calculateDiffSummary(items);

      return {
        snapshotId,
        snapshotDate: snapshot.createdAt instanceof Timestamp 
          ? snapshot.createdAt.toDate() 
          : new Date(snapshot.createdAt),
        filter: filter || { brand: 'all', deviceType: 'all' },
        summary,
        items,
        calculatedAt: new Date(),
        processingTimeMs: Date.now() - startTime
      };

    } catch (error) {
      console.error('❌ Diff 계산 실패:', error);
      throw new Error('변경사항 비교에 실패했습니다.');
    }
  }

  /**
   * 개별 항목 Diff 계산
   */
  private static calculateDiffItem(
    priceId: string,
    current: UnifiedPriceDocument | undefined,
    snapshot: UnifiedPriceDocument | undefined
  ): PriceDiffItem {
    const source = current || snapshot!;

    let diffType: DiffType;
    if (!current && snapshot) {
      diffType = 'removed'; // 현재에 없고 스냅샷에만 있음 → 복구 시 추가됨
    } else if (current && !snapshot) {
      diffType = 'added'; // 현재에만 있고 스냅샷에 없음 → 복구 시 삭제됨
    } else if (current && snapshot) {
      const pricesChanged = 
        current.gradePrices.A !== snapshot.gradePrices.A ||
        current.gradePrices.B !== snapshot.gradePrices.B ||
        current.gradePrices.C !== snapshot.gradePrices.C ||
        current.gradePrices.D !== snapshot.gradePrices.D;
      diffType = pricesChanged ? 'modified' : 'unchanged';
    } else {
      diffType = 'unchanged';
    }

    const item: PriceDiffItem = {
      priceId,
      modelId: source.modelId,
      modelName: source.variant?.storage ? `${source.modelId} ${source.variant.storage}` : source.modelId,
      brand: source.brand,
      deviceType: source.deviceType,
      storage: source.variant?.storage || '',
      connectivity: source.variant?.connectivity,
      diffType,
      currentPrices: current?.gradePrices,
      snapshotPrices: snapshot?.gradePrices
    };

    // 가격 변경 상세 계산
    if (diffType === 'modified' && current && snapshot) {
      item.priceChanges = (['A', 'B', 'C', 'D'] as const).map(grade => {
        const currentValue = current.gradePrices[grade] || 0;
        const snapshotValue = snapshot.gradePrices[grade] || 0;
        const difference = currentValue - snapshotValue;
        const percentChange = snapshotValue > 0 
          ? Math.round((difference / snapshotValue) * 100)
          : 0;

        return { grade, currentValue, snapshotValue, difference, percentChange };
      }).filter(c => c.difference !== 0);
    }

    return item;
  }

  /**
   * Diff 요약 계산
   */
  private static calculateDiffSummary(items: PriceDiffItem[]): DiffSummary {
    const summary: DiffSummary = {
      totalItems: items.length,
      added: 0,
      removed: 0,
      modified: 0,
      unchanged: 0,
      byBrand: {
        Apple: { added: 0, removed: 0, modified: 0 },
        Samsung: { added: 0, removed: 0, modified: 0 }
      },
      byDeviceType: {
        smartphone: { added: 0, removed: 0, modified: 0 },
        tablet: { added: 0, removed: 0, modified: 0 },
        wearable: { added: 0, removed: 0, modified: 0 }
      }
    };

    for (const item of items) {
      // 전체 카운트
      switch (item.diffType) {
        case 'added': summary.added++; break;
        case 'removed': summary.removed++; break;
        case 'modified': summary.modified++; break;
        case 'unchanged': summary.unchanged++; break;
      }

      // 브랜드별
      if (item.diffType !== 'unchanged') {
        const brandStats = summary.byBrand[item.brand];
        if (brandStats) {
          brandStats[item.diffType as 'added' | 'removed' | 'modified']++;
        }

        // 디바이스 타입별
        const deviceStats = summary.byDeviceType[item.deviceType];
        if (deviceStats) {
          deviceStats[item.diffType as 'added' | 'removed' | 'modified']++;
        }
      }
    }

    return summary;
  }

  // ============================================================
  // ✅ 데이터 검증
  // ============================================================

  /**
   * 가격 데이터 검증
   */
  static validatePriceData(data: UnifiedPriceDocument[]): ValidationResult {
    const issues: ValidationIssue[] = [];
    const startTime = Date.now();

    for (const price of data) {
      // 1. 가격 범위 검증
      const grades: ('A' | 'B' | 'C' | 'D')[] = ['A', 'B', 'C', 'D'];
      for (const grade of grades) {
        const value = price.gradePrices[grade];
        
        if (value === null || value === undefined) {
          issues.push({
            type: 'missing_price',
            severity: 'warning',
            message: `${grade}등급 가격 누락`,
            priceId: price.id,
            modelId: price.modelId,
            field: `gradePrices.${grade}`
          });
        } else if (value < SNAPSHOT_CONSTANTS.MIN_PRICE || value > SNAPSHOT_CONSTANTS.MAX_PRICE) {
          issues.push({
            type: 'invalid_price_range',
            severity: 'error',
            message: `${grade}등급 가격 범위 이상: ${value.toLocaleString()}원`,
            priceId: price.id,
            modelId: price.modelId,
            field: `gradePrices.${grade}`,
            currentValue: value,
            expectedValue: `${SNAPSHOT_CONSTANTS.MIN_PRICE} ~ ${SNAPSHOT_CONSTANTS.MAX_PRICE}`
          });
        }
      }

      // 2. 등급별 단조 감소 검증 (A ≥ B ≥ C ≥ D)
      const { A, B, C, D } = price.gradePrices;
      if (A !== null && B !== null && A < B) {
        issues.push({
          type: 'grade_monotonicity',
          severity: 'warning',
          message: `등급 가격 역전: A등급(${A?.toLocaleString()}) < B등급(${B?.toLocaleString()})`,
          priceId: price.id,
          modelId: price.modelId
        });
      }
      if (B !== null && C !== null && B < C) {
        issues.push({
          type: 'grade_monotonicity',
          severity: 'warning',
          message: `등급 가격 역전: B등급(${B?.toLocaleString()}) < C등급(${C?.toLocaleString()})`,
          priceId: price.id,
          modelId: price.modelId
        });
      }
      if (C !== null && D !== null && C < D) {
        issues.push({
          type: 'grade_monotonicity',
          severity: 'warning',
          message: `등급 가격 역전: C등급(${C?.toLocaleString()}) < D등급(${D?.toLocaleString()})`,
          priceId: price.id,
          modelId: price.modelId
        });
      }
    }

    // 3. 중복 검사
    const idCounts = new Map<string, number>();
    for (const price of data) {
      idCounts.set(price.id, (idCounts.get(price.id) || 0) + 1);
    }
    for (const [id, count] of idCounts) {
      if (count > 1) {
        issues.push({
          type: 'duplicate_entry',
          severity: 'error',
          message: `중복 항목 발견: ${id} (${count}개)`,
          priceId: id
        });
      }
    }

    const errorCount = issues.filter(i => i.severity === 'error').length;
    const warningCount = issues.filter(i => i.severity === 'warning').length;
    const infoCount = issues.filter(i => i.severity === 'info').length;

    let status: ValidationStatus;
    if (errorCount > 0) {
      status = 'error';
    } else if (warningCount > 0) {
      status = 'warning';
    } else {
      status = 'valid';
    }

    return {
      isValid: errorCount === 0,
      status,
      errorCount,
      warningCount,
      infoCount,
      issues,
      validatedAt: new Date(),
      totalRecordsChecked: data.length
    };
  }

  // ============================================================
  // 🔄 복구 실행
  // ============================================================

  /**
   * 스냅샷으로부터 복구 실행
   */
  static async restoreFromSnapshot(
    snapshotId: string,
    options: RestoreOptions,
    onProgress?: (progress: RestoreProgress) => void
  ): Promise<RestoreResult> {
    const startTime = Date.now();
    let preRestoreBackupId: string | undefined;

    const updateProgress = (progress: Partial<RestoreProgress>) => {
      if (onProgress) {
        onProgress({
          phase: 'preparing',
          currentStep: 0,
          totalSteps: 5,
          processedRecords: 0,
          totalRecords: 0,
          message: '준비 중...',
          ...progress
        });
      }
    };

    try {
      // 1단계: 스냅샷 데이터 로드
      updateProgress({ phase: 'preparing', currentStep: 1, message: '스냅샷 데이터 로드 중...' });
      const snapshotData = await this.getSnapshotData(snapshotId, options.filter);

      if (snapshotData.length === 0) {
        throw new Error('복구할 데이터가 없습니다.');
      }

      // 2단계: 검증 (선택사항)
      if (!options.skipValidation) {
        updateProgress({ phase: 'validating', currentStep: 2, message: '데이터 검증 중...' });
        const validation = this.validatePriceData(snapshotData);
        if (!validation.isValid) {
          throw new Error(`검증 실패: ${validation.errorCount}개 에러 발견`);
        }
      }

      // 3단계: 현재 데이터 백업 (선택사항)
      if (options.createBackupBefore) {
        updateProgress({ phase: 'backing_up', currentStep: 3, message: '현재 데이터 백업 중...' });
        const backupResult = await this.createSnapshot({
          description: `복구 전 자동 백업 (복구 대상: ${snapshotId})`,
          adminId: 'system_auto_backup',
          filter: options.filter
        });
        if (backupResult.success) {
          preRestoreBackupId = backupResult.snapshotId;
        }
      }

      // Dry run 모드면 여기서 종료
      if (options.dryRun) {
        return {
          success: true,
          snapshotId,
          restoredAt: new Date(),
          statistics: {
            totalRestored: snapshotData.length,
            added: 0,
            updated: 0,
            deleted: 0,
            skipped: 0,
            failed: 0
          },
          preRestoreBackupId,
          durationMs: Date.now() - startTime
        };
      }

      // 4단계: 복구 실행
      updateProgress({
        phase: 'restoring',
        currentStep: 4,
        message: '데이터 복구 중...',
        totalRecords: snapshotData.length
      });

      const statistics = {
        totalRestored: 0,
        added: 0,
        updated: 0,
        deleted: 0,
        skipped: 0,
        failed: 0
      };
      const errors: { priceId: string; error: string }[] = [];

      // 모드별 처리
      if (options.mode === 'replace') {
        // 기존 데이터 삭제 후 교체
        const currentData = await this.fetchAllPrices(options.filter);
        const snapshotIds = new Set(snapshotData.map(p => p.id));

        // 스냅샷에 없는 현재 데이터 삭제
        for (const current of currentData) {
          if (!snapshotIds.has(current.id)) {
            try {
              await deleteDoc(doc(db, this.UNIFIED_PRICES_COLLECTION, current.id));
              statistics.deleted++;
            } catch (e) {
              errors.push({ priceId: current.id, error: '삭제 실패' });
              statistics.failed++;
            }
          }
        }
      }

      // 스냅샷 데이터 저장
      const currentData = await this.fetchAllPrices(options.filter);
      const currentMap = new Map(currentData.map(p => [p.id, p]));

      for (let i = 0; i < snapshotData.length; i++) {
        const price = snapshotData[i];
        
        try {
          const existing = currentMap.get(price.id);
          
          // Timestamp 필드 정리
          const cleanPrice = {
            ...price,
            updatedAt: serverTimestamp(),
            restoredFrom: snapshotId,
            restoredAt: serverTimestamp()
          };

          await setDoc(doc(db, this.UNIFIED_PRICES_COLLECTION, price.id), cleanPrice);
          
          if (existing) {
            statistics.updated++;
          } else {
            statistics.added++;
          }
          statistics.totalRestored++;

        } catch (e) {
          errors.push({ 
            priceId: price.id, 
            error: e instanceof Error ? e.message : '저장 실패' 
          });
          statistics.failed++;
        }

        // 진행률 업데이트 (50개마다)
        if ((i + 1) % 50 === 0 || i === snapshotData.length - 1) {
          updateProgress({
            phase: 'restoring',
            currentStep: 4,
            processedRecords: i + 1,
            totalRecords: snapshotData.length,
            message: `복구 중... (${i + 1}/${snapshotData.length})`
          });
        }
      }

      // 5단계: 완료
      updateProgress({
        phase: 'completed',
        currentStep: 5,
        processedRecords: snapshotData.length,
        totalRecords: snapshotData.length,
        message: '복구 완료!'
      });

      return {
        success: errors.length === 0,
        snapshotId,
        restoredAt: new Date(),
        statistics,
        preRestoreBackupId,
        errors: errors.length > 0 ? errors : undefined,
        durationMs: Date.now() - startTime
      };

    } catch (error) {
      console.error('❌ 복구 실패:', error);
      
      updateProgress({
        phase: 'failed',
        message: error instanceof Error ? error.message : '복구 실패',
        errors: [error instanceof Error ? error.message : '알 수 없는 오류']
      });

      return {
        success: false,
        snapshotId,
        restoredAt: new Date(),
        statistics: {
          totalRestored: 0,
          added: 0,
          updated: 0,
          deleted: 0,
          skipped: 0,
          failed: 0
        },
        preRestoreBackupId,
        errors: [{ priceId: '', error: error instanceof Error ? error.message : '복구 실패' }],
        durationMs: Date.now() - startTime
      };
    }
  }

  // ============================================================
  // 🗑️ 스냅샷 삭제
  // ============================================================

  /**
   * 스냅샷 삭제 (메타데이터 + 데이터 청크)
   */
  static async deleteSnapshot(snapshotId: string): Promise<void> {
    try {
      // 1. 데이터 청크 삭제
      const chunksRef = collection(db, this.COLLECTION, snapshotId, this.DATA_SUBCOLLECTION);
      const chunksSnapshot = await getDocs(chunksRef);
      
      const batch = writeBatch(db);
      chunksSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      await batch.commit();

      // 2. 메타데이터 삭제
      await deleteDoc(doc(db, this.COLLECTION, snapshotId));

      console.log(`🗑️ 스냅샷 삭제 완료: ${snapshotId}`);

    } catch (error) {
      console.error(`❌ 스냅샷 삭제 실패 (${snapshotId}):`, error);
      throw new Error('스냅샷 삭제에 실패했습니다.');
    }
  }

  // ============================================================
  // 🛠️ 유틸리티 메서드
  // ============================================================

  /**
   * 스냅샷 ID 생성
   */
  private static generateSnapshotId(): string {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
    const timeStr = now.toISOString().slice(11, 19).replace(/:/g, '');
    const random = Math.random().toString(36).substring(2, 6);
    return `snapshot_${dateStr}_${timeStr}_${random}`;
  }

  /**
   * 현재 모든 가격 데이터 조회 (레거시 컬렉션 포함)
   * 활성화된 가격 데이터만 조회 (isActive !== false)
   * 
   * ⚠️ 중요: 실제 가격 데이터는 레거시 컬렉션들에 저장되어 있음
   * - grade_prices_apple (Apple 스마트폰)
   * - grade_prices_samsung (Samsung 스마트폰)
   * - grade_prices_apple_tablet (Apple 태블릿)
   * - grade_prices_samsung_tablet (Samsung 태블릿)
   * - grade_prices_apple_wearable (Apple 웨어러블)
   */
  private static async fetchAllPrices(filter?: SnapshotFilter): Promise<UnifiedPriceDocument[]> {
    console.log('🔍 가격 데이터 전체 조회 시작 (레거시 컬렉션 포함)...');
    
    let allPrices: UnifiedPriceDocument[] = [];

    // 1️⃣ 먼저 unified_prices 컬렉션 조회 (있다면)
    try {
      const unifiedSnapshot = await getDocs(collection(db, this.UNIFIED_PRICES_COLLECTION));
      const unifiedPrices = unifiedSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as UnifiedPriceDocument[];
      console.log(`📦 unified_prices: ${unifiedPrices.length}개`);
      allPrices.push(...unifiedPrices);
    } catch (error) {
      console.log('⚠️ unified_prices 컬렉션 조회 실패 (비어있을 수 있음)');
    }

    // 2️⃣ 레거시 컬렉션들 조회
    for (const legacyConfig of this.LEGACY_COLLECTIONS) {
      try {
        const legacySnapshot = await getDocs(collection(db, legacyConfig.collection));
        
        if (legacySnapshot.docs.length > 0) {
          const legacyPrices = legacySnapshot.docs.map(doc => {
            const data = doc.data();
            return this.convertLegacyToUnified(doc.id, data, legacyConfig.brand, legacyConfig.deviceType);
          });
          
          console.log(`📦 ${legacyConfig.collection}: ${legacyPrices.length}개 (${legacyConfig.brand} ${legacyConfig.deviceType})`);
          allPrices.push(...legacyPrices);
        }
      } catch (error) {
        console.warn(`⚠️ ${legacyConfig.collection} 컬렉션 조회 실패:`, error);
      }
    }

    console.log(`📊 전체 가격 데이터 합계: ${allPrices.length}개`);

    // 3️⃣ 중복 제거 (ID 기준, unified_prices가 우선)
    const priceMap = new Map<string, UnifiedPriceDocument>();
    for (const price of allPrices) {
      // 이미 존재하는 경우 unified_prices 데이터 우선 (deviceType이 정확하게 설정된 경우)
      const existing = priceMap.get(price.id);
      if (!existing || (price.deviceType && !existing.deviceType)) {
        priceMap.set(price.id, price);
      }
    }
    let uniquePrices = Array.from(priceMap.values());
    console.log(`📊 중복 제거 후: ${uniquePrices.length}개`);

    // 4️⃣ 활성화된 데이터만 필터링
    const totalCount = uniquePrices.length;
    uniquePrices = uniquePrices.filter(p => p.isActive !== false);
    console.log(`✅ 활성화된 가격 데이터: ${uniquePrices.length}개 (비활성: ${totalCount - uniquePrices.length}개)`);

    // 5️⃣ 필터 적용
    if (filter) {
      uniquePrices = this.applyFilter(uniquePrices, filter);
      console.log(`🔍 필터 적용 후: ${uniquePrices.length}개`);
    }

    return uniquePrices;
  }

  /**
   * 레거시 데이터를 UnifiedPriceDocument 형식으로 변환
   */
  private static convertLegacyToUnified(
    docId: string,
    data: any,
    brand: DeviceBrand,
    deviceType: DeviceType
  ): UnifiedPriceDocument {
    // storage 문자열에서 connectivity 분리 (예: "256GB - Wi-Fi" → storage: "256GB", connectivity: "Wi-Fi")
    const storageStr = data.storage || '기본';
    const storageParts = storageStr.split(' - ').map((p: string) => p.trim());
    const storage = storageParts[0] || '기본';
    const connectivity = storageParts.length >= 2 ? storageParts[1] : undefined;

    // 모델ID 생성 (기기명 + 브랜드 기반)
    const deviceModel = data.deviceModel || data.modelName || docId;
    const modelId = `${brand.toLowerCase()}-${deviceType}-${deviceModel.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-가-힣]/g, '')}`;
    
    // 고유 가격 ID 생성
    const priceId = `${modelId}_${storage.replace(/\s+/g, '')}${connectivity ? `_${connectivity.replace(/\s+/g, '')}` : ''}_KR`;

    return {
      id: priceId,
      modelId,
      deviceType,
      brand,
      region: 'KR',
      currency: 'KRW',
      variant: {
        storage,
        connectivity: connectivity as any,
        color: data.color,
        caseSize: data.caseSize
      },
      gradePrices: {
        A: data.gradePrices?.A ?? data.prices?.A ?? 0,
        B: data.gradePrices?.B ?? data.prices?.B ?? 0,
        C: data.gradePrices?.C ?? data.prices?.C ?? 0,
        D: data.gradePrices?.D ?? data.prices?.D ?? 0
      },
      isActive: data.isActive !== false,
      version: 'live',
      createdAt: data.createdAt?.toDate?.() || data.createdAt || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt || new Date(),
      updatedBy: data.updatedBy || 'legacy-system',
      memo: data.memo,
      // 레거시 원본 정보 보존
      legacy: {
        originalCollection: `legacy_${brand.toLowerCase()}_${deviceType}`,
        originalId: docId,
        migratedAt: new Date()
      }
    } as UnifiedPriceDocument;
  }

  /**
   * 필터 적용
   */
  private static applyFilter(
    data: UnifiedPriceDocument[],
    filter: SnapshotFilter
  ): UnifiedPriceDocument[] {
    return data.filter(price => {
      if (filter.brand && filter.brand !== 'all' && price.brand !== filter.brand) {
        return false;
      }
      if (filter.deviceType && filter.deviceType !== 'all' && price.deviceType !== filter.deviceType) {
        return false;
      }
      if (filter.modelIds && filter.modelIds.length > 0 && !filter.modelIds.includes(price.modelId)) {
        return false;
      }
      return true;
    });
  }

  /**
   * 브랜드/디바이스별 분포 계산
   * 다양한 데이터 형식 지원 (대소문자, 한글/영문 등)
   */
  private static calculateBreakdown(prices: UnifiedPriceDocument[]): SnapshotBreakdown {
    const breakdown: SnapshotBreakdown = {
      'Apple-smartphone': 0,
      'Apple-tablet': 0,
      'Apple-wearable': 0,
      'Samsung-smartphone': 0,
      'Samsung-tablet': 0
    };

    // 🔧 브랜드 정규화 함수
    const normalizeBrand = (brand: string | undefined): 'Apple' | 'Samsung' | 'LG' | null => {
      if (!brand) return null;
      const lower = brand.toLowerCase();
      if (lower === 'apple' || lower === '애플') return 'Apple';
      if (lower === 'samsung' || lower === '삼성') return 'Samsung';
      if (lower === 'lg' || lower === 'LG') return 'LG';
      return null;
    };

    // 🔧 디바이스 타입 정규화 함수
    const normalizeDeviceType = (deviceType: string | undefined): 'smartphone' | 'tablet' | 'wearable' | null => {
      if (!deviceType) return null;
      const lower = deviceType.toLowerCase();
      if (lower === 'smartphone' || lower === '스마트폰' || lower === 'phone') return 'smartphone';
      if (lower === 'tablet' || lower === '태블릿' || lower === 'ipad') return 'tablet';
      if (lower === 'wearable' || lower === '웨어러블' || lower === 'airpods' || lower === 'watch') return 'wearable';
      return null;
    };

    let unmatchedCount = 0;
    const unmatchedSamples: { brand: any; deviceType: any }[] = [];

    for (const price of prices) {
      const normalizedBrand = normalizeBrand(price.brand);
      const normalizedDeviceType = normalizeDeviceType(price.deviceType);

      if (normalizedBrand && normalizedDeviceType) {
        const key = `${normalizedBrand}-${normalizedDeviceType}` as keyof SnapshotBreakdown;
        if (key in breakdown) {
          breakdown[key]++;
        }
      } else {
        unmatchedCount++;
        if (unmatchedSamples.length < 5) {
          unmatchedSamples.push({ brand: price.brand, deviceType: price.deviceType });
        }
      }
    }

    // 디버깅: 분포 로그 출력
    console.log('📊 가격 데이터 브랜드/디바이스별 분포:', breakdown);
    console.log(`📊 총 ${prices.length}개 중 매칭된 항목: ${prices.length - unmatchedCount}개`);
    if (unmatchedCount > 0) {
      console.warn(`⚠️ 매칭되지 않은 항목: ${unmatchedCount}개`, unmatchedSamples);
    }

    return breakdown;
  }

  /**
   * 빈 분포 객체 반환
   */
  private static getEmptyBreakdown(): SnapshotBreakdown {
    return {
      'Apple-smartphone': 0,
      'Apple-tablet': 0,
      'Apple-wearable': 0,
      'Samsung-smartphone': 0,
      'Samsung-tablet': 0
    };
  }

  /**
   * 데이터를 청크로 분할
   */
  private static splitIntoChunks(data: UnifiedPriceDocument[]): UnifiedPriceDocument[][] {
    const chunks: UnifiedPriceDocument[][] = [];
    for (let i = 0; i < data.length; i += this.CHUNK_SIZE) {
      chunks.push(data.slice(i, i + this.CHUNK_SIZE));
    }
    return chunks;
  }

  /**
   * 파일 크기 추정 (bytes)
   */
  private static estimateFileSize(data: UnifiedPriceDocument[]): number {
    // 대략적인 JSON 크기 추정: 레코드당 약 500바이트
    return data.length * 500;
  }
}

// 싱글톤 export
export default PriceSnapshotService;

